import React from "react";
import { UserPlus, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { HomepageEra } from "@/config/home-era-list";

interface QuickStartFabProps {
  eras: HomepageEra[];
  shouldShowNewBadge: (eraId: string, newUntil?: string) => boolean;
  onSelect: (era: HomepageEra) => void;
  onBrowseAll?: () => void;
  /**
   * Scroll distance after which the FAB collapses to icon-only.
   * Accepts a number (px) or a CSS length string ("5rem", "20", "120px").
   * Defaults to Tailwind spacing-20 (`5rem` = 80px at the default root font size),
   * which lines up with the homepage hero's first content offset.
   */
  collapseAfter?: number | string;
}

const DEFAULT_COLLAPSE_AFTER = "5rem"; // = spacing-20 in Tailwind

/**
 * Material 3 expressive FAB sizing by viewport width.
 * - Compact  (< 600px):  small  (40dp)
 * - Medium   (600-839):  medium (56dp / standard FAB)
 * - Expanded (>= 840px): large  (96dp)
 * Mirrors the M3 adaptive FAB guidance.
 */
type FabSize = "small" | "medium" | "large";
const useResponsiveFabSize = (): FabSize => {
  const get = (): FabSize => {
    if (typeof window === "undefined") return "small";
    const w = window.innerWidth;
    if (w >= 840) return "large";
    if (w >= 600) return "medium";
    return "small";
  };
  const [size, setSize] = React.useState<FabSize>(get);
  React.useEffect(() => {
    const onResize = () => setSize(get());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
};

/** Resolve a px / rem / unitless value to pixels relative to the document root. */
const resolveCollapsePx = (value: number | string): number => {
  if (typeof value === "number") return value;
  const trimmed = value.trim();
  if (trimmed.endsWith("rem")) {
    const n = parseFloat(trimmed);
    const root =
      typeof window !== "undefined"
        ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
        : 16;
    return n * root;
  }
  return parseFloat(trimmed) || 0;
};

const QuickStartFab: React.FC<QuickStartFabProps> = ({
  eras,
  shouldShowNewBadge,
  onSelect,
  onBrowseAll,
  collapseAfter = DEFAULT_COLLAPSE_AFTER,
}) => {
  const { t } = useTranslation("home");
  const [open, setOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const fabSize = useResponsiveFabSize();
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const fabRef = React.useRef<HTMLElement | null>(null);

  // React stringifies boolean props on custom elements (extended={false} -> "false"),
 // which CSS still matches as :host([extended]). Toggle the attribute imperatively.
  React.useEffect(() => {
    const el = fabRef.current;
    if (!el) return;
    if (collapsed) {
      el.removeAttribute("extended");
    } else {
      el.setAttribute("extended", "");
    }
  }, [collapsed]);

  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const threshold = resolveCollapsePx(collapseAfter);

    // Find the nearest scrollable ancestor so the FAB works regardless of
    // whether the page scrolls on window or on an inner overflow container.
    const findScrollParent = (node: HTMLElement | null): HTMLElement | null => {
      let el = node?.parentElement ?? null;
      while (el && el !== document.body) {
        const style = getComputedStyle(el);
        const overflowY = style.overflowY;
        const scrollable =
          (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
          el.scrollHeight > el.clientHeight;
        if (scrollable) return el;
        el = el.parentElement;
      }
      return null;
    };

    const scrollParent = findScrollParent(sentinel);
    const target: HTMLElement | Window = scrollParent ?? window;

    const getScrollTop = () =>
      scrollParent
        ? scrollParent.scrollTop
        : window.scrollY || document.documentElement.scrollTop || 0;

    const onScroll = () => setCollapsed(getScrollTop() > threshold);
    onScroll();

    target.addEventListener("scroll", onScroll, { passive: true } as AddEventListenerOptions);
    return () => target.removeEventListener("scroll", onScroll as EventListener);
  }, [collapseAfter]);

  const handleSelect = (era: HomepageEra) => {
    if (!era.available) return;
    setOpen(false);
    onSelect(era);
  };

  const handleBrowse = () => {
    setOpen(false);
    onBrowseAll?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div ref={sentinelRef} aria-hidden="true" className="hidden" />
      {open && (
        <div
          aria-hidden="true"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-40 bg-black/50 animate-in fade-in-0 duration-300"
        />
      )}
      <PopoverTrigger
        aria-label={t("quickStart.create")}
        className="m3-brand-scope fixed bottom-6 right-[calc(1.5rem+var(--removed-body-scroll-bar-size,0px))] z-50 bg-transparent border-0 p-0 cursor-pointer focus:outline-none"
      >
        <m3e-fab
          ref={fabRef as never}
          variant="primary-container"
          size={fabSize}
          aria-hidden="true"
        >
          <UserPlus aria-hidden="true" className="text-teal-800" />
          <span slot="label">{t("quickStart.create")}</span>
        </m3e-fab>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={12}
        className="group w-[320px] max-w-[calc(100vw-2rem)] p-0 bg-card border border-brand-cream/30 shadow-[0_0_40px_rgba(0,0,0,0.6)] overflow-hidden origin-bottom-right will-change-transform data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-[0.97] data-[state=open]:slide-in-from-bottom-2 data-[state=closed]:slide-out-to-bottom-1 data-[state=open]:duration-[420ms] data-[state=closed]:duration-[360ms] data-[state=open]:ease-[cubic-bezier(0.05,0.7,0.1,1)] data-[state=closed]:ease-[cubic-bezier(0.3,0,0.8,0.15)]"
      >
        {/* Ornate top accent */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-brand-green/60 to-transparent" />

        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-brand-cream/10">
          <h3 className="uppercase tracking-[0.3em] text-brand-cream font-antiqua text-sm font-serif font-semibold">
            {t("quickStart.heading")}
          </h3>
        </div>

        <ul className="py-2">
          {eras.map((era, index) => {
            const Icon = era.icon;
            const showNew = shouldShowNewBadge(era.id, era.newUntil);
            const disabled = !era.available;
            const exitDelay = (eras.length - 1 - index) * 35;
            return (
              <li
                key={era.id}
                className="opacity-0 group-data-[state=open]:animate-slide-fade-in group-data-[state=closed]:animate-fade-out motion-reduce:opacity-100 motion-reduce:animate-none"
                style={{
                  animationDelay: open ? `${120 + index * 60}ms` : `${exitDelay}ms`,
                  animationFillMode: "both",
                  animationDuration: open ? undefined : "180ms",
                }}
              >
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSelect(era)}
                  className={`group w-full flex items-center gap-4 px-5 py-3 text-left transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-green/50 ${
                    disabled
                      ? "opacity-60 cursor-not-allowed"
                      : "hover:bg-brand-green/10 cursor-pointer"
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border bg-accent/40 transition-colors ${
                      disabled
                        ? "border-brand-cream/10"
                        : showNew
                          ? "border-brand-green/40 ring-1 ring-brand-green/30 group-hover:border-brand-green"
                          : "border-brand-cream/20 group-hover:border-brand-green/50"
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${disabled ? "text-brand-cream/30" : era.iconColor}`} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                      <span className={`block text-sm font-semibold uppercase tracking-wide font-antiqua truncate ${disabled ? "text-brand-cream/50" : "text-brand-cream"} font-sans`}>
                        {t(era.titleKey)}
                      </span>
                      {showNew && era.available && (
                        <span className="text-[9px] font-bold uppercase tracking-wider text-brand-green border border-brand-green/40 rounded-sm px-1.5 py-0.5">
                          {t("quickStart.new")}
                        </span>
                      )}
                    </span>
                    {disabled ? (
                      <span className="block text-[10px] uppercase tracking-[0.2em] text-brand-cream/40 mt-0.5">
                        {t("quickStart.comingSoon")}
                      </span>
                    ) : (
                      <span className="block italic text-brand-cream/40 truncate leading-tight text-xs">
                        {t(era.subtitleKey)}
                      </span>
                    )}
                  </span>
                  {!disabled && (
                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-brand-cream/20 group-hover:text-brand-green group-hover:translate-x-1 transition-all" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

export default QuickStartFab;